import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import type { CampaignDetail, MailboxRow } from '../outreachTypes';
import { QuestionPanel } from './QuestionPanel';
import { firstWeekPlan } from './firstWeekPlan';
import { whoMessages as messages } from './whoMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for step 2; each part uses its own slice of it */
import styles from './Who.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FirstWeekProps {
  campaign: CampaignDetail;
  /** The first mailbox of the campaign, or null (the ramp of a new mailbox). */
  mailbox: MailboxRow | null;
}

function dots(count: number, className: string | undefined, prefix: string): React.ReactNode[] {
  return Array.from({ length: count }, (_, index) => <i key={`${prefix}${index}`} className={className} />);
}

/** "Your first week" (design outreach-v2 c1): the pitches of the next 7 days, with the warm-up. */
const FirstWeekComponent: React.FC<FirstWeekProps> = ({ campaign, mailbox }) => {
  const intl = useIntl();
  const days = useMemo(
    () =>
      firstWeekPlan({
        now: new Date(),
        rate: campaign.dailyRate,
        weekdaysOnly: campaign.schedule.weekdaysOnly,
        followUpDays: campaign.followUpDays,
        releaseDate: campaign.releaseDate,
        mailboxConnectedAt: mailbox === null ? null : new Date(mailbox.connectedAt),
      }).map((day) => {
        const key = intl.formatDate(day.day, { weekday: 'short', day: 'numeric', timeZone: 'UTC' }).toUpperCase();
        const line = () => {
          if (day.off) {
            return intl.formatMessage(messages.weekNone);
          }
          if (day.followUps > 0) {
            return intl.formatMessage(messages.weekFollowUps, { count: day.pitches, followUps: day.followUps });
          }
          return intl.formatMessage(day.warmup ? messages.weekWarmup : messages.weekCount, { count: day.pitches });
        };
        return (
          <div
            key={day.day.toISOString()}
            className={clsx(styles.day, day.isRelease && styles.dayRelease, day.off && styles.dayOff)}
          >
            <span className={styles.dayKey}>
              {day.isRelease ? intl.formatMessage(messages.weekOut, { day: key }) : key}
            </span>
            {!day.off && (
              <div className={styles.dots}>
                {dots(day.pitches, styles.dotPitch, 'p')}
                {dots(day.followUps, styles.dotFollow, 'f')}
              </div>
            )}
            <small className="text-body-secondary">{line()}</small>
          </div>
        );
      }),
    [campaign, intl, mailbox]
  );

  return (
    <QuestionPanel title={intl.formatMessage(messages.weekTitle)} hint={intl.formatMessage(messages.weekHint)}>
      <div className={styles.week} data-testid="first-week">
        {days}
      </div>
      <div className={styles.legend}>
        <span>
          <i className={styles.dotPitch} />
          {intl.formatMessage(messages.legendNew)}
        </span>
        <span>
          <i className={styles.dotFollow} />
          {intl.formatMessage(messages.legendFollowUp)}
        </span>
      </div>
    </QuestionPanel>
  );
};

export const FirstWeek = TypedMemo(FirstWeekComponent);
